import { useMutation, useQuery, useQueryClient } from "react-query";
import { http } from "src/api/http";
import { BrandData, TwilioPhoneNumber, TwilioRegStatus, TwilioUserRegStatus, TwilioConfig } from "./models";
import { API_ROUTE } from "./constant";

export function useBrandDataAPI() {
  return useQuery<BrandData>({
    queryKey: ["useBrandData"],
    queryFn: () => http(API_ROUTE)
  });
}

export function useBrandRegistrationStatusAPI(
  enabled: boolean,
  refetchInterval: number | false = false,
  onSuccess?: (response: TwilioRegStatus) => void) {
  return useQuery<TwilioRegStatus>({
    queryKey: ["useBrandRegistrationStatus"],
    queryFn: () => http('/api/10dlc-campaign-registry/twilio-reg-status'),
    onSuccess: (response: TwilioRegStatus) => onSuccess?.(response),
    refetchInterval: refetchInterval,
    enabled: enabled
  });
}

export function useUserTwilioRegistrationStatusAPI(identityId: string | undefined) {
  return useQuery<TwilioUserRegStatus>({
    queryKey: ["userTwilioRegStatus", identityId],
    queryFn: () => http(`/api/admin/user-twilio-registration-status/${identityId}`),
    enabled: !!identityId
  });
}

export function useTwilioConfigAPI() {
  return useQuery<TwilioConfig>({
    queryKey: ["useTwilioConfig"],
    queryFn: () => http("/api/account/twilio-config")
  });
}

export function useCustomerTwilioConfigAPI(identityId: string | undefined) {
  return useQuery<TwilioConfig>({
    queryKey: ["customerTwilioConfig", identityId],
    queryFn: () => http(`/api/admin/user-twilio-config/${identityId}`),
    enabled: !!identityId
  });
}

export function useUpdateCustomerTwilioConfigAPI() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { identityId: string, additionalMessages: number }>({
    mutationFn: ({ identityId, additionalMessages }) => 
      http(`/api/admin/user-twilio-config/${identityId}`, {
        method: 'PUT',
        body: { additionalMessages }
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["customerTwilioConfig", variables.identityId]);
    }
  });
}

export function useBrandDataUpdateAPI(onSuccess?: (response: TwilioRegStatus) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BrandData) => http(API_ROUTE, {
      method: 'POST',
      body,
    }),
    onSuccess: (response: TwilioRegStatus) => {
      queryClient.invalidateQueries(['useBrandData']);
      queryClient.invalidateQueries(['useBrandRegistrationStatus']);
      if (onSuccess) {
        onSuccess(response);
      }
    }
  });
}

export function useTwilioAvailableNumbersAPI() {
  return useQuery<TwilioPhoneNumber[]>({
    queryKey: ["useAvailableNumbers"],
    queryFn: () => http("/api/10dlc-campaign-registry/twilio-available-phone-numbers")
  });
}

export function useTwilioNumbersForPurchaseAPI(contains: string) {
  return useQuery<TwilioPhoneNumber[]>({
    queryKey: ["useNumbersForPurchase", contains],
    queryFn: () => http(`/api/10dlc-campaign-registry/twilio-purchase-phone-numbers?contains=${contains}`)
  });
}

export function useAttachTwilioNumber(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { phoneNumber: string }) => http(`/api/10dlc-campaign-registry/twilio-attach-phone-number/${body.phoneNumber}`, {
      method: 'POST',
      body,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['useBrandRegistrationStatus']);
      queryClient.invalidateQueries(['useAvailableNumbers']);
      onSuccess?.();
    }
  });
}
